import logger from '../utils/logger';
import React, { createContext, useState, useEffect, useContext } from 'react';
import { authGet, authPut } from '../services/authApi';
import { useAuth } from './AuthContext';

const FeatureFlagContext = createContext();

export const useFeatureFlags = () => useContext(FeatureFlagContext);

export const FeatureFlagProvider = ({ children }) => {
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();

  const fetchFeatures = async () => {
    setIsLoading(true);
    try {
      const response = await authGet('/api/feature-flags');
      if (response.ok) {
        const data = await response.json();
        setFeatures(data);
      } else {
        logger.error('Failed to fetch feature flags');
        setFeatures([]);
      }
    } catch (error) {
      logger.error('Error fetching feature flags:', error);
      setFeatures([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchFeatures();
    } else {
      // Reset features when user logs out or is not authenticated
      setFeatures([]);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.userId, user?.tier]); // Re-fetch when user or tier changes

  const updateFeature = async (id, enabled) => {
    try {
      const response = await authPut(`/api/feature-flags/${id}`, { enabled });
      if (response.ok) {
        const updatedFeature = await response.json();
        setFeatures(features.map(f => f._id === id ? updatedFeature : f));
      } else {
        logger.error('Failed to update feature flag');
      }
    } catch (error) {
      logger.error('Error updating feature flag:', error);
    }
  };

  const isFeatureEnabled = (featureName) => {
    const feature = features.find(f => f.name === featureName);
    if (!feature || !feature.enabled) {
      return false;
    }

    if (feature.tier === 'pro' && (!user || user.tier !== 'pro')) {
      return false;
    }

    return true;
  };

  return (
    <FeatureFlagContext.Provider value={{ features, isLoading, updateFeature, isFeatureEnabled, refreshFeatures: fetchFeatures }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
